import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { ChannelStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { CHANNEL_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { cn, formatStatusLabel } from "@/lib/utils";
import { listChannels } from "@/api/channels";
import { listMerchants } from "@/api/merchants";
import { getInvoiceAssetOptions } from "@/api/invoices";
import { type ChannelListItem, type ListChannelsParams } from "@/api/types";

// Task 14 (feature-031 Phase B): platform-wide Channels list — mirrors
// _app.invoices.tsx's list-page shape (Task 26) exactly: useTableState for
// URL-persisted page/sort/filters, a status quick-filter plus a "More
// filters" dialog for merchant/asset/search. The merchant/asset filter
// options reuse listMerchants/getInvoiceAssetOptions (MERCHANTS_VIEWER /
// INVOICES_VIEWER reads respectively) — same graceful-degradation precedent
// _app.invoices.tsx already relies on: a caller who lacks that specific role
// simply sees an empty dropdown, not a broken page (this page itself is
// gated at CHANNELS_VIEWER by the shell's nav).
type ChannelFilters = {
  status?: ChannelStatus;
  merchantId?: string;
  assetId?: string;
  search?: string;
};

const DEFAULT_FILTERS: ChannelFilters = {};

const DIALOG_FILTER_KEYS = ["merchantId", "assetId", "search"] as const satisfies ReadonlyArray<
  keyof ChannelFilters
>;

function validateChannelsSearch(search: Record<string, unknown>): TableSearch<ChannelFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as ChannelStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    assetId: typeof search.assetId === "string" ? search.assetId : undefined,
    search: typeof search.search === "string" ? search.search : undefined,
  };
}

export const Route = createFileRoute("/_app/channels")({
  validateSearch: validateChannelsSearch,
  component: ChannelsPage,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

const SELECT_CLASSNAME = cn(
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

const INPUT_CLASSNAME = cn(
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

function RowLink({ channelId, children }: { channelId: string; children: ReactNode }) {
  return (
    <Link to="/channels/$channelId" params={{ channelId }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

function ChannelFiltersDialog({
  filters,
  setFilter,
  merchants,
  assetOptions,
}: {
  filters: ChannelFilters;
  setFilter: <K extends keyof ChannelFilters>(key: K, value: ChannelFilters[K]) => void;
  merchants: Array<{ id: string; businessName: string }>;
  assetOptions: Array<{ id: string; symbol: string; network: string }>;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = DIALOG_FILTER_KEYS.filter((key) => filters[key] !== undefined).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Filter channels</DialogTitle>
          <DialogDescription>Narrow the list by merchant, asset, or a publicId/externalId/label search.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="filter-merchant">Merchant</Label>
            <select
              id="filter-merchant"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.merchantId ?? "ALL"}
              onChange={(e) => setFilter("merchantId", e.target.value === "ALL" ? undefined : e.target.value)}
            >
              <option value="ALL">All merchants</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.businessName}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-asset">Asset</Label>
            <select
              id="filter-asset"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.assetId ?? "ALL"}
              onChange={(e) => setFilter("assetId", e.target.value === "ALL" ? undefined : e.target.value)}
            >
              <option value="ALL">All assets</option>
              {assetOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.symbol} · {formatStatusLabel(a.network)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="filter-search">Search</Label>
            <input
              id="filter-search"
              placeholder="Public ID, external ID, or label"
              className={INPUT_CLASSNAME}
              value={filters.search ?? ""}
              onChange={(e) => setFilter("search", e.target.value || undefined)}
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={activeCount === 0}
            onClick={() => DIALOG_FILTER_KEYS.forEach((key) => setFilter(key, undefined))}
          >
            Clear these filters
          </Button>
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelsPage() {
  const tableState = useTableState<ChannelFilters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });

  const params: ListChannelsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListChannelsParams["sortBy"],
    sortOrder: tableState.sortOrder,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["channels", params],
    queryFn: () => listChannels(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const { data: assetOptions } = useQuery({
    queryKey: ["invoice-asset-options"],
    queryFn: getInvoiceAssetOptions,
  });

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<ChannelListItem>[] = [
    {
      key: "publicId",
      header: "Channel ID",
      cell: (c) => (
        <RowLink channelId={c.id}>
          <span className="font-mono group-hover:underline">{c.publicId}</span>
        </RowLink>
      ),
    },
    {
      key: "merchant",
      header: "Merchant",
      cell: (c) => (
        <MerchantCell merchantId={c.merchant.id} businessName={c.merchant.businessName} publicId={c.merchant.publicId} />
      ),
    },
    {
      key: "label",
      header: "External ID",
      cell: (c) => (
        <RowLink channelId={c.id}>
          <span className="block">{c.label}</span>
          {c.externalId && <span className="block text-xs text-muted-foreground">{c.externalId}</span>}
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (c) => (
        <RowLink channelId={c.id}>
          <AssetLabel symbol={c.asset.symbol} network={c.asset.network} />
        </RowLink>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (c) => (
        <RowLink channelId={c.id}>
          <StatusBadge label={formatStatusLabel(c.status)} color={CHANNEL_STATUS_COLOR[c.status]} />
        </RowLink>
      ),
    },
    {
      key: "address",
      header: "Address",
      cell: (c) => <HashLink value={c.address} type="address" network={c.asset.network} className="text-xs" />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      cell: (c) => <RowLink channelId={c.id}>{formatDate(c.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load channels." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        filterBar={
          <>
            <select
              aria-label="Status"
              className={SELECT_CLASSNAME}
              value={tableState.filters.status ?? "ALL"}
              onChange={(e) =>
                tableState.setFilter("status", e.target.value === "ALL" ? undefined : (e.target.value as ChannelStatus))
              }
            >
              <option value="ALL">All statuses</option>
              {Object.values(ChannelStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>

            <ChannelFiltersDialog
              filters={tableState.filters}
              setFilter={tableState.setFilter}
              merchants={sortedMerchants}
              assetOptions={assetOptions ?? []}
            />

            {anyFilterActive && (
              <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
                Clear filters
              </Button>
            )}
          </>
        }
        getRowKey={(c) => c.id}
        emptyMessage="No channels match your filters."
      />
    </div>
  );
}
